import { ArrowBack } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import GetAppIcon from '@mui/icons-material/GetApp';
// import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Slide, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import * as React from 'react';

// import { copyTextToClipboard, downloadAsFile, useIsWidthUp } from 'react-router';

// import { DataAPIContext, GET_ONE } from '../api';
import { copyTextToClipboard, downloadAsFile, useIsWidthUp } from '../utils';

// LogViewer renders inside high z-index dialogs (its own row-details dialog,
// ImportPreview's), so its tooltips must sit above them.
const tooltipPopperProps = { style: { zIndex: 100000000 } };

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const LogViewer = (props) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [query, setQuery] = React.useState('');
    const [rowDetails, setRowDetails] = React.useState(null);
    const isSmallScreen = !useIsWidthUp('sm')

    const onChangeQuery = (ev) => { setQuery(ev.target.value.trim()); }
    let queryRegex = null;
    try {
        queryRegex = new RegExp(query, "i");
    } catch (e) {
        console.warn("Error parsing regexp", query, e);
    }
    const filteredLogs = props.logs.filter(line => {
        if (!query || !query.length) { return true; }
        if (queryRegex) {
            return queryRegex.test(line.message);
        } else {
            return line.message.indexOf(query) >= 0;
        }
    });
    const logsFiltered = props.logs.length > filteredLogs.length;

    const onRowClick = (params, event, details) => {
        console.log('onRowClick', params, event, details);
        setRowDetails(params.row);
    }

    const onRowDetailsClose = () => { setRowDetails(null); }

    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    const filteredLogsAsText = (fl) => {
        return fl.map(x => (`${x.date} ${x.time} ${x.host} ${x.lineno} ${x.level} ${x.message}`)).join("\n");
    }

    const filteredLogsAsJSON = (fl) => {
        return JSON.stringify(fl, null, 4);
    }

    const downloadRawLogs = (ev) => {
        const suffix = logsFiltered ? 'filtered-logs' : 'logs';
        const filename = props.filenamePrefix ? `${props.filenamePrefix}-${suffix}.txt` : `${suffix}.txt`;
        downloadAsFile(filename, filteredLogsAsText(filteredLogs));
    }

    const downloadJSONLogs = (ev) => {
        const suffix = logsFiltered ? 'filtered-logs' : 'logs';
        const filename = props.filenamePrefix ? `${props.filenamePrefix}-${suffix}.json` : `${suffix}.json`;
        downloadAsFile(filename, filteredLogsAsJSON(filteredLogs));
    }

    const copyLogsToClipboard = (ev) => {
        copyToClipboard(filteredLogsAsText(filteredLogs), "Logs");
    }

    return (<>
        {rowDetails && <>
            <Dialog fullWidth maxWidth="xl" open={true} onClose={onRowDetailsClose} TransitionComponent={Transition} sx={{ zIndex: 99999999 }}>
                <DialogTitle>
                    <Tooltip title="Back" PopperProps={tooltipPopperProps}><IconButton onClick={onRowDetailsClose}><ArrowBack /></IconButton></Tooltip>
                    Log line {rowDetails.id}
                </DialogTitle>
                <DialogContent>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12} sm={3}>{rowDetails.ts ? moment.utc(rowDetails.ts).format() : `${rowDetails.date ?? ''} ${rowDetails.time ?? ''}`}</Grid>
                        <Grid item xs={12} sm={3}>{rowDetails.lineno}</Grid>
                        <Grid item xs={12} sm={3}>{rowDetails.host}</Grid>
                        <Grid item xs={12} sm={3}>{rowDetails.level}</Grid>
                        <Grid item xs={12}>
                            <hr />
                            <Box sx={{ height: props.height, overflowY: 'scroll', overflowX: 'scroll' }}>
                                <pre>
                                    {rowDetails.message}
                                </pre>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </>}
        <Grid container direction="row" spacing={0}>
            <Grid item xs={12}>
                <TextField
                    autoFocus
                    name="query"
                    fullWidth
                    size="small"
                    label="Search logs"
                    variant="outlined"
                    value={query}
                    onChange={onChangeQuery}
                    InputProps={{
                        endAdornment: (
                            <Tooltip title="Clear search" PopperProps={tooltipPopperProps}>
                                <IconButton
                                    onClick={() => setQuery('')}
                                    edge="end"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        )
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ height: props.height, width: '100%', mt: theme.spacing(2) }}>
                    <DataGrid
                        autoHeight={false}
                        checkboxSelection={false}
                        density="compact"
                        rowHeight={36}
                        pagination={false}
                        onRowClick={onRowClick}
                        rows={filteredLogs}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    host: false,
                                    lineno: false,
                                }
                            }
                        }}
                        columns={[
                            {
                                field: 'id', headerName: 'ID', flex: 0.2,
                                renderCell: p => <Typography variant="caption">{p.row.id}</Typography>
                            },
                            {
                                field: 'ts', headerName: "Timestamp", flex: 1,
                                renderCell: p => <Typography variant="caption">{p.row.ts ? moment.utc(p.row.ts).format() : `${p.row.date ?? ''} ${p.row.time ?? ''}`}</Typography>
                            },
                            {
                                field: "host", headerName: "Host", flex: 1,
                                renderCell: p => <Typography variant="caption">{p.row.host}</Typography>
                            },
                            {
                                field: 'lineno', headerName: 'Line No', flex: 1.5,
                                renderCell: p => <Typography variant="caption">{p.row.lineno}</Typography>
                            },
                            {
                                field: "message", headerName: "Message", flex: 4,
                                renderCell: p => <Typography variant="caption">{p.row.message}</Typography>
                            },
                        ]}
                    />
                </Box>
            </Grid>
            <Grid item xs={12} container direction="row" spacing={2} mt={2}>
                <Grid item xs={12} sm={4}>
                    <Button variant="outlined" color="primary" onClick={downloadRawLogs}>
                        <GetAppIcon />&nbsp;Download {logsFiltered ? 'filtered ' : ''}raw logs
                    </Button>
                </Grid>
                <Grid item xs={12} sm={4} container={!isSmallScreen} justifyContent="center">
                    <Button variant="outlined" color="primary" onClick={downloadJSONLogs}>
                        <GetAppIcon />&nbsp;Download {logsFiltered ? 'filtered ' : ''}JSON logs
                    </Button>
                </Grid>
                <Grid item xs={12} sm={4} container={!isSmallScreen} justifyContent="flex-end">
                    <Button variant="outlined" color="primary" onClick={copyLogsToClipboard}>
                        <FileCopyOutlinedIcon />&nbsp;Copy {logsFiltered ? 'filtered ' : ''}logs to clipboard</Button>
                </Grid>
            </Grid>
        </Grid>
    </>);
}

export default LogViewer;
